import React from "react";

const ThemeContext = React.createContext({ theme: "dark", setTheme: () => {}, toggle: () => {} });

const KEY = "vlyt.theme";

function readSystem() {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function readInitial() {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {}
  return readSystem();
}

function applyTheme(theme) {
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", theme === "light" ? "#F7F7F2" : "#0E0E12");
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = React.useState(readInitial);
  // hasExplicitPreference: true once user manually toggles. Then we ignore OS changes.
  const [hasExplicit, setHasExplicit] = React.useState(() => {
    try {
      return !!window.localStorage.getItem(KEY);
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Auto-follow the OS preference until the user makes an explicit choice.
  React.useEffect(() => {
    if (hasExplicit || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = (e) => setThemeState(e.matches ? "light" : "dark");
    // sync once in case the value changed between render and effect run
    setThemeState(mq.matches ? "light" : "dark");
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, [hasExplicit]);

  const setTheme = React.useCallback((next) => {
    setThemeState(next);
    setHasExplicit(true);
    try {
      window.localStorage.setItem(KEY, next);
    } catch {}
  }, []);

  const toggle = React.useCallback(() => {
    setThemeState((t) => {
      const next = t === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(KEY, next);
      } catch {}
      return next;
    });
    setHasExplicit(true);
  }, []);

  const value = React.useMemo(() => ({ theme, setTheme, toggle }), [theme, setTheme, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return React.useContext(ThemeContext);
}
